import { useNavigate } from "react-router-dom";
import { ArrowUpRight, FlaskConical, LayoutTemplate, Moon, WandSparkles } from "lucide-react";
import {
  Card,
  Editorial,
  InsightCard,
  LineChart,
  LiveDot,
  NaraAvatar,
  StatCard,
} from "@/ui";
import { account, insights as sampleInsights, overnight as sampleOvernight, todayKpis, useLiveVisitors } from "@/mock";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useDigest, useInsights, useRealtime, useSummary, useTimeseries } from "@/data/hooks";
import { LiveStream } from "./LiveStream";
import { overnightIcon } from "./overnight";
import { kpisFromSummary, pointsFromTimeseries } from "./mapping";

/** Tiny mono provenance chip so live vs sample is never ambiguous. */
function SourceChip({ live }: { live: boolean }) {
  return (
    <span className="font-mono text-[10px] lowercase tracking-wide text-subtle">
      {live ? "live" : "sample"}
    </span>
  );
}

const HOUR = new Date().getHours();
const SALUTATION = HOUR < 12 ? "Morning" : HOUR < 18 ? "Afternoon" : "Evening";
const COFFEE = HOUR < 12 ? "here's what moved while you were away." : "here's what's moving right now.";

const QUICK_ACTIONS = [
  {
    icon: WandSparkles,
    label: "Make a page",
    hint: "Describe it, Nara builds it",
    to: "/make",
    prompt: "Make a new landing page.",
  },
  {
    icon: FlaskConical,
    label: "Start a test",
    hint: "Split traffic, let it run",
    to: "/make",
    prompt: "Start an A/B test of the homepage hero.",
  },
  {
    icon: LayoutTemplate,
    label: "Open Signals",
    hint: "The full analytics view",
    to: "/signals",
    prompt: undefined,
  },
] as const;

export function TodayScreen() {
  const nav = useNavigate();
  const { current, siteId, range, live: wsLive } = useWorkspace();
  const first = account.user.name.split(" ")[0];

  // --- live ribbon: realtime.active when live, simulated drift otherwise ---
  const realtimeQ = useRealtime(siteId);
  const { count: sampleCount } = useLiveVisitors();
  const ribbonLive = wsLive && !!siteId && !realtimeQ.isError && realtimeQ.data !== undefined;
  const onSiteNow = ribbonLive ? realtimeQ.data!.active : sampleCount;

  // --- headline KPIs + today chart: summary + visits timeseries ---
  const summaryQ = useSummary(siteId, range);
  const seriesQ = useTimeseries(siteId, range, "visits");
  const realVisits = pointsFromTimeseries(seriesQ.data);

  const kpisLive =
    wsLive && !!siteId && !summaryQ.isError && summaryQ.data !== undefined;
  const kpis = kpisLive ? kpisFromSummary(summaryQ.data!, realVisits) : todayKpis;
  const kpisLoading = !!siteId && wsLive && summaryQ.isLoading;

  const chartLive = wsLive && !!siteId && !seriesQ.isError && realVisits.length > 0;
  const visitorsKpi = kpis[0]!;
  const chartSeries = chartLive ? realVisits : todayKpis[0]!.series;
  const chartLoading = !!siteId && wsLive && seriesQ.isLoading;

  // --- Nara feed: overnight digest + insights (live when the property has real
  // traffic; the backend flags `seeded:true` for its demo fallback). When the
  // query has no data (no live property / error) we show the local sample set.
  const digestQ = useDigest(siteId, range);
  const digestItems = digestQ.data?.items ?? sampleOvernight;
  const digestLive = !!digestQ.data && !digestQ.data.seeded;

  const insightsQ = useInsights(siteId, range);
  const insightItems = insightsQ.data?.items ?? sampleInsights;
  const insightsLive = !!insightsQ.data && !insightsQ.data.seeded;

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="stagger space-y-6 pb-4">
      {/* greeting + live visitor ribbon */}
      <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="eyebrow mb-1.5">
            Today · {today} · {current.name}
          </p>
          <h1 className="max-w-2xl text-[27px] font-semibold leading-[1.12] tracking-tight text-ink-strong md:text-[34px]">
            {SALUTATION}, {first}.{" "}
            <Editorial className="font-normal text-muted">{COFFEE}</Editorial>
          </h1>
        </div>

        <div className="flex shrink-0 items-center gap-2.5 self-start rounded-full border border-hairline bg-paper px-4 py-2.5 shadow-sm dark:shadow-none md:self-auto">
          <LiveDot />
          <span className="text-[18px] font-semibold tabular-nums leading-none text-ink-strong">
            {onSiteNow}
          </span>
          <span className="text-[13px] text-muted">on site now</span>
          <span className="ml-1">
            <SourceChip live={ribbonLive} />
          </span>
        </div>
      </header>

      {/* headline numbers */}
      {kpisLoading ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="flex h-[148px] flex-col gap-3 p-4">
              <div className="h-2.5 w-20 animate-pulse rounded bg-paper-2 dark:bg-white/5" />
              <div className="mt-1 h-8 w-24 animate-pulse rounded bg-paper-2 dark:bg-white/5" />
              <div className="mt-auto h-[34px] w-full animate-pulse rounded bg-paper-2 dark:bg-white/5" />
            </Card>
          ))}
        </div>
      ) : (
        <div className="relative">
          {!kpisLive && (
            <div className="absolute -top-4 right-0">
              <SourceChip live={false} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {kpis.map((k) => (
              <StatCard key={k.key} kpi={k} />
            ))}
          </div>
        </div>
      )}

      {/* today chart + live stream */}
      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="p-5 lg:col-span-3">
          <div className="mb-1 flex items-start justify-between">
            <div>
              <p className="eyebrow mb-1 flex items-center gap-2">
                Visitors today · hourly <SourceChip live={chartLive} />
              </p>
              <h3 className="text-[15px] font-semibold tracking-tight text-ink-strong">
                {visitorsKpi.delta > 0
                  ? `Traffic is tracking up +${visitorsKpi.delta}%`
                  : visitorsKpi.delta < 0
                    ? `Traffic is tracking down ${visitorsKpi.delta}%`
                    : "Traffic today"}
              </h3>
            </div>
            <button
              onClick={() => nav("/signals")}
              className="inline-flex items-center gap-1 text-[13px] text-muted transition-colors hover:text-ink"
            >
              Details <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            </button>
          </div>
          {chartLoading ? (
            <div className="h-[200px] w-full animate-pulse rounded-md bg-paper-2 dark:bg-white/5" />
          ) : (
            <LineChart a={chartSeries} height={200} />
          )}
        </Card>

        <Card className="p-5 lg:col-span-2">
          <LiveStream />
        </Card>
      </div>

      {/* While you slept — Nara overnight digest */}
      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <NaraAvatar size="sm" />
            <div>
              <div className="flex items-center gap-1.5">
                <Moon className="h-3.5 w-3.5 text-muted" strokeWidth={1.5} />
                <h3 className="text-[15px] font-semibold tracking-tight text-ink-strong">
                  While you slept
                </h3>
              </div>
              <p className="text-[12px] text-muted">
                Nara worked {digestItems.length} {digestItems.length === 1 ? "thing" : "things"} overnight
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-subtle">last night · 02:00–06:00</span>
            <SourceChip live={digestLive} />
          </div>
        </div>

        <ul className="space-y-1">
          {digestItems.map((o) => {
            const Icon = overnightIcon[o.kind];
            return (
              <li
                key={o.id}
                className="flex items-start gap-3 rounded-btn px-2 py-2.5"
              >
                <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-paper-2 text-muted dark:bg-white/5">
                  <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-medium leading-snug text-ink-strong">{o.title}</p>
                  <p className="text-[13px] leading-relaxed text-muted">{o.detail}</p>
                </div>
                <span className="mt-0.5 shrink-0 font-mono text-[11px] tabular-nums text-subtle">
                  {o.time}
                </span>
              </li>
            );
          })}
        </ul>
      </Card>

      {/* the loop: Nara noticed */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-[16px] font-semibold tracking-tight text-ink-strong">Nara noticed</h2>
              <SourceChip live={insightsLive} />
            </div>
            <p className="text-[13px] text-muted">
              {insightItems.length} {insightItems.length === 1 ? "thing" : "things"} worth a look — tap to fix in Make
            </p>
          </div>
          <button
            onClick={() => nav("/signals")}
            className="inline-flex shrink-0 items-center gap-1 text-[13px] text-muted transition-colors hover:text-ink"
          >
            Open Signals <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </button>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {insightItems.map((ins) => (
            <InsightCard
              key={ins.id}
              insight={ins}
              onAsk={() => nav("/make", { state: { prompt: ins.makePrompt } })}
            />
          ))}
        </div>
      </section>

      {/* quick actions */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold tracking-tight text-ink-strong">Quick start</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          {QUICK_ACTIONS.map((a) => {
            const Icon = a.icon;
            return (
              <Card
                key={a.label}
                interactive
                onClick={() => nav(a.to, a.prompt ? { state: { prompt: a.prompt } } : undefined)}
                className="group flex min-h-[44px] items-center gap-3 p-4"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-paper-2 text-ink-strong transition-colors group-hover:text-accent dark:bg-white/5">
                  <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-medium text-ink-strong">{a.label}</p>
                  <p className="truncate text-[12px] text-muted">{a.hint}</p>
                </div>
                <ArrowUpRight
                  className="h-4 w-4 shrink-0 text-subtle transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  strokeWidth={1.5}
                />
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}
